'use client';

import type { ConvexReactClient } from 'convex/react';

import { api } from '@/convex/_generated/api';

import {
  BrowserUploadError,
  browserUploadPartCount,
  browserUploadPartRange,
  hashBrowserUploadParts,
  postBrowserUploadOperation,
  putBrowserUploadPart,
  uploadUrlOf,
} from './browser-multipart-upload-transport';
import {
  newBrowserUploadAttemptId,
  ownsBrowserUploadSession,
  parseBrowserUploadControl,
} from './browser-upload-session-client';

export { BrowserUploadError } from './browser-multipart-upload-transport';
export type { BrowserUploadErrorCode } from './browser-multipart-upload-transport';

/**
 * Host upload client for app binaries (plan §6.2, T4A_API.md §1). Bytes go
 * browser -> upload proxy -> R2 in 64 MiB parts; Convex only sees the
 * session control calls and, later, the storage key. The workspace quota is
 * the only size limit: `begin` holds the declared size and abort releases it.
 */

export const BROWSER_UPLOAD_PART_BYTES = 64 * 1024 * 1024;
/** Per part: the first try and three retries. */
export const BROWSER_UPLOAD_PART_ATTEMPTS = 4;

const CONTROL_PREFIX = '/api/browser-upload-sessions/';

export type BrowserUploadProgress = {
  loadedBytes: number;
  totalBytes: number;
  partNumber: number;
  partCount: number;
};

/** The hash pass result: what `begin` declares and each part's Content-MD5. */
export type BrowserUploadIdentity = {
  contentHash: string;
  sizeBytes: number;
  partMd5s: string[];
};

type UploadOptions = {
  contentType: string;
  signal?: AbortSignal;
  onProgress?: (progress: BrowserUploadProgress) => void;
  /** An identity hashed in an earlier page lifetime; skips the hash pass. */
  identity?: BrowserUploadIdentity;
  /**
   * Parts already PUT to this same session in an earlier page lifetime. They
   * are not sent again: `complete` rebuilds the part ETags from the stored
   * part MD5s. Ignored for a single-part session, whose `complete` needs the
   * part ETag.
   */
  completedParts?: ReadonlyArray<number>;
  /** Called once with the identity, before `begin`. */
  onIdentity?: (identity: BrowserUploadIdentity) => void;
  /** Called after each part PUT succeeds. */
  onPartDone?: (partNumber: number) => void;
};

function retryDelayMs(attempt: number): number {
  return Math.min(8_000, 500 * 2 ** (attempt - 1));
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new BrowserUploadError('UPLOAD_ABORTED'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function asUploadError(
  error: unknown,
  signal?: AbortSignal
): BrowserUploadError {
  if (signal?.aborted) return new BrowserUploadError('UPLOAD_ABORTED');
  if (error instanceof BrowserUploadError) return error;
  return new BrowserUploadError(
    'UPLOAD_SESSION_FAILED',
    error instanceof Error ? error.message : String(error)
  );
}

/**
 * Upload a Blob into an open browser upload session (the `uploadUrl` that
 * `generatePrivateUploadUrlForMedia` returned): hash, `begin` (quota hold),
 * PUT each part with progress and per-part retry, then `complete`. Abort or
 * any failure calls `abort`, which releases the hold.
 */
async function uploadBlobToBrowserSessionOnce(
  uploadUrl: string,
  blob: Blob,
  options: UploadOptions
): Promise<{
  sessionId: string;
  objectEtag: string;
  sizeBytes: number;
  contentHash: string;
}> {
  const control = parseBrowserUploadControl(uploadUrl);
  if (!control)
    throw new BrowserUploadError(
      'UPLOAD_SESSION_FAILED',
      'not a browser upload session'
    );
  const sessionId = control.endpoint.slice(CONTROL_PREFIX.length);
  const { signal } = options;
  const attemptId = newBrowserUploadAttemptId();
  let ownsSession = false;
  try {
    const identity =
      options.identity && options.identity.sizeBytes === blob.size
        ? options.identity
        : await hashBrowserUploadParts(blob, BROWSER_UPLOAD_PART_BYTES, signal);
    options.onIdentity?.(identity);
    const partCount = browserUploadPartCount(
      identity.sizeBytes,
      BROWSER_UPLOAD_PART_BYTES
    );
    const multipart = partCount > 1;
    // Always `begin`, even when resuming: a replayed begin renews the lease.
    const skipParts = new Set(multipart ? (options.completedParts ?? []) : []);
    const begun = await postBrowserUploadOperation(
      control,
      {
        operation: 'begin',
        attemptId,
        contentType: options.contentType,
        contentHash: identity.contentHash,
        sizeBytes: identity.sizeBytes,
        contentMd5: identity.partMd5s[0],
        ...(multipart
          ? {
              partMd5s: identity.partMd5s,
              partSizeBytes: BROWSER_UPLOAD_PART_BYTES,
            }
          : {}),
      },
      signal
    );
    // Abort only a session this attempt began: a failed begin, or a replay
    // of another caller's session (owner: false), must never delete it.
    ownsSession = ownsBrowserUploadSession(begun);
    let doneBytes = 0;
    let singlePartEtag: string | null = null;
    for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
      const range = browserUploadPartRange(
        identity.sizeBytes,
        BROWSER_UPLOAD_PART_BYTES,
        partNumber
      );
      const body = blob.slice(range.start, range.end);
      if (skipParts.has(partNumber)) {
        doneBytes += body.size;
        options.onProgress?.({
          loadedBytes: doneBytes,
          totalBytes: identity.sizeBytes,
          partNumber,
          partCount,
        });
        continue;
      }
      const contentMd5 = identity.partMd5s[partNumber - 1] ?? '';
      for (let attempt = 1; ; attempt += 1) {
        try {
          // Part 1's first URL came with `begin`; every other try asks for a
          // fresh one (which also renews the session lease).
          const signedUrl =
            partNumber === 1 && attempt === 1
              ? uploadUrlOf(begun)
              : uploadUrlOf(
                  await postBrowserUploadOperation(
                    control,
                    { operation: 'part', partNumber },
                    signal
                  )
                );
          const etag = await putBrowserUploadPart({
            signedUrl,
            body,
            contentType: options.contentType,
            contentMd5,
            signal,
            onLoaded: (loaded) =>
              options.onProgress?.({
                loadedBytes: doneBytes + loaded,
                totalBytes: identity.sizeBytes,
                partNumber,
                partCount,
              }),
          });
          if (!multipart) singlePartEtag = etag;
          options.onPartDone?.(partNumber);
          break;
        } catch (error) {
          const failure = asUploadError(error, signal);
          if (
            failure.code !== 'UPLOAD_PART_FAILED' ||
            attempt >= BROWSER_UPLOAD_PART_ATTEMPTS
          ) {
            throw failure;
          }
          await wait(retryDelayMs(attempt), signal);
        }
      }
      doneBytes += body.size;
    }
    const completed = await postBrowserUploadOperation(
      control,
      {
        operation: 'complete',
        ...(singlePartEtag ? { partEtag: singlePartEtag } : {}),
      },
      signal
    );
    const objectEtag =
      typeof completed.objectEtag === 'string' ? completed.objectEtag : '';
    return {
      sessionId,
      objectEtag,
      sizeBytes: identity.sizeBytes,
      contentHash: identity.contentHash,
    };
  } catch (error) {
    // Release the quota hold and the R2 multipart upload. Not tied to the
    // caller's signal: an abort must still reach the server.
    if (ownsSession)
      await postBrowserUploadOperation(control, {
        operation: 'abort',
        attemptId,
      }).catch(() => undefined);
    throw asUploadError(error, signal);
  }
}

type BrowserUploadResult = Awaited<
  ReturnType<typeof uploadBlobToBrowserSessionOnce>
>;

/** One in-flight upload of one Blob to one session, and the callers on it. */
type SharedBrowserUpload = {
  contentType: string;
  controller: AbortController;
  callers: Set<UploadOptions>;
  upload: Promise<BrowserUploadResult>;
};

const activeBrowserUploads = new Map<string, Map<Blob, SharedBrowserUpload>>();

function startSharedUpload(
  uploadUrl: string,
  blob: Blob,
  options: UploadOptions
): SharedBrowserUpload {
  const { contentType, identity, completedParts, onIdentity, onPartDone } =
    options;
  const controller = new AbortController();
  const callers = new Set<UploadOptions>();
  // Resume state and its callbacks come from the caller that starts it.
  const upload = uploadBlobToBrowserSessionOnce(uploadUrl, blob, {
    contentType,
    ...(identity ? { identity } : {}),
    ...(completedParts ? { completedParts } : {}),
    ...(onIdentity ? { onIdentity } : {}),
    ...(onPartDone ? { onPartDone } : {}),
    signal: controller.signal,
    onProgress: (progress) => {
      for (const caller of callers) caller.onProgress?.(progress);
    },
  });
  const shared = { contentType, controller, callers, upload };
  const perUrl = activeBrowserUploads.get(uploadUrl) ?? new Map();
  perUrl.set(blob, shared);
  activeBrowserUploads.set(uploadUrl, perUrl);
  const clear = () => forgetSharedUpload(uploadUrl, blob, shared);
  void upload.then(clear, clear);
  return shared;
}

function forgetSharedUpload(
  uploadUrl: string,
  blob: Blob,
  shared: SharedBrowserUpload
): void {
  const perUrl = activeBrowserUploads.get(uploadUrl);
  if (perUrl?.get(blob) !== shared) return;
  perUrl.delete(blob);
  if (perUrl.size === 0) activeBrowserUploads.delete(uploadUrl);
}

/**
 * Share one in-flight upload between remount/retry callers sending the same
 * Blob (and content type) to one session, so a remounted caller sees the
 * upload finish instead of racing it. Each caller keeps its own progress and
 * cancellation: its signal rejects only its own call, and the upload itself
 * is cancelled only once every caller has aborted. Anything else runs as its
 * own attempt; the server refuses a second identity, and a same-bytes replay
 * answers `owner: false`, so that attempt can never abort another's upload.
 */
export function uploadBlobToBrowserSession(
  uploadUrl: string,
  blob: Blob,
  options: UploadOptions
): Promise<BrowserUploadResult> {
  if (options.signal?.aborted)
    return Promise.reject(new BrowserUploadError('UPLOAD_ABORTED'));
  const active = activeBrowserUploads.get(uploadUrl)?.get(blob);
  if (active && active.contentType !== options.contentType)
    return uploadBlobToBrowserSessionOnce(uploadUrl, blob, options);
  const shared = active ?? startSharedUpload(uploadUrl, blob, options);
  const caller: UploadOptions = { ...options };
  shared.callers.add(caller);
  return new Promise((resolve, reject) => {
    const leave = () => {
      caller.signal?.removeEventListener('abort', onAbort);
      return shared.callers.delete(caller);
    };
    const onAbort = () => {
      if (!leave()) return;
      if (shared.callers.size === 0) {
        // Nobody is waiting any more: a later caller starts a fresh upload.
        forgetSharedUpload(uploadUrl, blob, shared);
        shared.controller.abort();
      }
      reject(new BrowserUploadError('UPLOAD_ABORTED'));
    };
    caller.signal?.addEventListener('abort', onAbort, { once: true });
    shared.upload.then(
      (result) => {
        if (leave()) resolve(result);
      },
      (error: unknown) => {
        if (leave()) reject(error);
      }
    );
  });
}

type UploadMediaType = 'image' | 'video' | 'audio' | 'file' | 'three-d';

function mediaTypeFor(contentType: string): UploadMediaType {
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('audio/')) return 'audio';
  return 'file';
}

/**
 * Open a private session as the signed-in viewer and upload the Blob.
 * Returns the storage key for `registerAppBinaryWrite` / `registerAppFileSave`.
 */
export async function uploadPrivateBlobAsViewer(
  convex: Pick<ConvexReactClient, 'mutation'>,
  args: {
    orgSlug: string;
    blob: Blob;
    filename: string;
    contentType: string;
    mediaType?: UploadMediaType;
    signal?: AbortSignal;
    onProgress?: (progress: BrowserUploadProgress) => void;
  }
): Promise<{
  storageKey: string;
  sessionId: string;
  sizeBytes: number;
  contentHash: string;
  contentType: string;
}> {
  if (args.signal?.aborted) throw new BrowserUploadError('UPLOAD_ABORTED');
  const reservation = await convex
    .mutation(api.media.uploads.generatePrivateUploadUrlForMedia, {
      orgSlug: args.orgSlug,
      mediaType: args.mediaType ?? mediaTypeFor(args.contentType),
      filename: args.filename,
      contentType: args.contentType,
      flow: 'workspace-app-file',
    })
    .catch((error: unknown) => {
      throw asUploadError(error);
    });
  const uploaded = await uploadBlobToBrowserSession(
    reservation.uploadUrl,
    args.blob,
    {
      contentType: args.contentType,
      ...(args.signal ? { signal: args.signal } : {}),
      ...(args.onProgress ? { onProgress: args.onProgress } : {}),
    }
  );
  return {
    storageKey: reservation.storageKey,
    sessionId: uploaded.sessionId,
    sizeBytes: uploaded.sizeBytes,
    contentHash: uploaded.contentHash,
    contentType: args.contentType,
  };
}
